import { useRef, useCallback, useEffect } from 'react';
import { MEASURE_ELEMENT_CONFIG } from '../constants';

/**
 * テキスト幅測定用のカスタムフック
 * @returns テキスト幅測定関数
 */
export const useTextMeasurement = () => {
  // 測定用要素をコンポーネント内で管理
  const measureElementRef = useRef<HTMLDivElement | null>(null);

  /**
   * 測定用要素を取得（初回作成または再利用）
   */
  const getMeasureElement = useCallback((): HTMLDivElement => {
    if (!measureElementRef.current) {
      measureElementRef.current = document.createElement('div');
      Object.assign(measureElementRef.current.style, MEASURE_ELEMENT_CONFIG);
      document.body.appendChild(measureElementRef.current);
    }
    return measureElementRef.current;
  }, []);

  /**
   * テキスト幅測定用の関数（測定要素を再利用してパフォーマンス向上）
   * @param texts 測定するテキストの配列
   * @param totalSpacing パディング+ボーダーの合計値
   * @returns 最大幅（スペース込み）
   */
  const measureMaxTextWidth = useCallback((texts: string[], totalSpacing: number): number => {
    const element = getMeasureElement();

    let maxTextWidth = 0;
    texts.forEach(text => {
      element.textContent = text;
      const width = element.offsetWidth;
      maxTextWidth = Math.max(maxTextWidth, width);
    });

    return maxTextWidth + totalSpacing;
  }, [getMeasureElement]);

  /**
   * 単一のテキスト幅を測定
   * @param text 測定するテキスト
   * @param totalSpacing パディング+ボーダーの合計値
   * @returns 幅（スペース込み）
   */
  const measureTextWidth = useCallback((text: string, totalSpacing: number): number => {
    if (!text) return 0;
    return measureMaxTextWidth([text], totalSpacing);
  }, [measureMaxTextWidth]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (measureElementRef.current && measureElementRef.current.parentNode) {
        measureElementRef.current.parentNode.removeChild(measureElementRef.current);
        measureElementRef.current = null;
      }
    };
  }, []);

  return {
    measureMaxTextWidth,
    measureTextWidth
  };
};
